import type {
  ArticlePickerArticle,
  ArticlePickerProduct,
  ArticleFilters as BackendArticleFilters,
  BrandSupplierDto,
  FilterFacets,
} from '@/ApiClient.ts';
import {
  dropCount,
  dropPredicateSet,
  intersectAll,
  outPriceAmountCounts,
  pricePredicateSets,
  type OutPriceAmountCount,
} from '@lib/facet-counts.ts';
import { useArticleStatuses } from '@/store/article-statuses.ts';
import { useComponentTypes } from '@/store/component-types.ts';
import { useDrops } from '@/store/drops.ts';
import { useHierarchy } from '@/store/hierarchy.ts';
import { useProductCategories } from '@/store/product-categories.ts';
import { useSeasons } from '@/store/seasons.ts';
import { useTags } from '@/store/tags.ts';
import { useUser } from '@/store/user';
import { useApi } from '@lib/api.ts';
import { genders } from '@lib/genders.ts';
import { whereNotNull } from '@lib/type-helpers.ts';
import isEqual from 'lodash/isEqual';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export type ArticleFilterOptions = {
  colors?: any[];
  suppliers: BrandSupplierDto[];
};

export type ArticleFilters = Required<BackendArticleFilters>;

export const useArticleFilters = defineStore('article-filter', () => {
  const api = useApi();
  const filterOptions = ref<ArticleFilterOptions>({
    colors: [],
    suppliers: [],
  });

  async function loadFilters() {
    await loadColors();
  }

  const loadColors = async function () {
    filterOptions.value.colors = await api.overview_GetColors(useUser().brandId);
  };

  function getInitialFilters(): ArticleFilters {
    return {
      searchTerm: '',
      hierarchyNodeIds: [],
      productCategoryIds: [],
      genders: [],
      seasonIds: [],
      colors: [],
      supplierIds: [],
      dropDateRange: null!,
      dropNames: [],
      statusIds: useArticleStatuses()
        .loaded.filter((s) => !s.excludeFromDefaultFiltering)
        .map((s) => s.id),

      tagIds: [],
      customFieldOptionIds: {},
      createdByUserIds: [],
      assignedUserIds: [],
      colorGroupIds: [],
      sizeSystemIds: [],
      outPriceAmounts: {},
      outPriceRanges: {},
      timelineHealth: [],
      nextGateRoleIds: [],
      nextGateNames: [],
    };
  }

  function getActiveFiltersCount(filters: ArticleFilters) {
    return Object.keys(filters).reduce((count, key) => {
      const typedKey = key as keyof typeof filters;
      if (typedKey === 'dropDateRange') {
        return count + (filters[typedKey] != null ? 1 : 0);
      }
      if (typedKey === 'customFieldOptionIds') {
        return count + Object.values(filters[typedKey]).filter((v) => v.length > 0).length;
      }
      if (typedKey === 'outPriceAmounts') {
        return count + Object.values(filters[typedKey]).filter((v) => v.length > 0).length;
      }
      if (typedKey === 'outPriceRanges') {
        return count + Object.values(filters[typedKey]).filter((v) => v.min != null || v.max != null).length;
      }
      if (Array.isArray(filters[typedKey])) {
        const diffCount = filters[typedKey].length - (getInitialFilters()[typedKey] as unknown[]).length;
        return count + (diffCount !== 0 ? Math.abs(diffCount) : 0);
      }
      if (typeof filters[typedKey] === 'string' && filters[typedKey].trim() !== '') {
        return count + 1;
      }
      return count;
    }, 0);
  }

  function filterMatchesArticlePickerArticle(
    filters: ArticleFilters,
    product: ArticlePickerProduct,
    article: ArticlePickerArticle
  ): boolean {
    const searchQuery = filters.searchTerm?.toLowerCase() ?? '';

    const hierarchyNodes = product.hierarchyNodeId ? useHierarchy().getNodeAndAncestors(product.hierarchyNodeId) : [];
    const articleTextValues: string[] = [
      product.name,
      article.articleNumber,
      useArticleStatuses().byId[article.statusId].name,
      ...hierarchyNodes.map((node) => node.name),
      ...article.tagIds.map((tagId) => useTags().byId[tagId].name),
      ...article.seasonIds.map((seasonId) => useSeasons().byId[seasonId].name),
      article.color?.name,
      ...article.genders
        .filter((gender) => gender in genders)
        .map((gender) => genders[gender as keyof typeof genders]?.name),
      useComponentTypes().getSubTypeFromId(article.principalMaterialId)?.fullName,
      ...article.skus.map((sku) => sku.sizeValue),
      useProductCategories().byId[article.productCategoryId ?? -1]?.name,
    ].filter(whereNotNull);

    if (!articleTextValues.some((textValue) => textValue.toLowerCase().includes(searchQuery))) return false;

    if (filters.seasonIds.length > 0 && !filters.seasonIds.some((seasonId) => article.seasonIds.includes(seasonId)))
      return false;

    if (filters.tagIds.length > 0 && !filters.tagIds.some((tagId) => article.tagIds.includes(tagId))) return false;

    if (filters.genders.length > 0 && !filters.genders.some((gender) => article.genders.includes(gender))) return false;

    if (filters.statusIds.length > 0 && !filters.statusIds.includes(article.statusId)) return false;

    if (
      filters.hierarchyNodeIds.length > 0 &&
      !filters.hierarchyNodeIds.some((nodeId) => hierarchyNodes.some((hn) => hn.id === nodeId))
    )
      return false;

    if (
      filters.supplierIds.length > 0 &&
      !filters.supplierIds.some((supplierId) => article.supplierIds.includes(supplierId))
    )
      return false;

    if (filters.dropDateRange) {
      const drops = useDrops().byRange(filters.dropDateRange);

      if (!drops.some((drop) => article.dropIds.includes(drop.id))) return false;
    }

    if (filters.dropNames.length > 0) {
      const drops = useDrops();
      const articleDropNames = article.dropIds.map((id) => drops.byId[id]?.name).filter(Boolean) as string[];
      if (!filters.dropNames.some((name) => articleDropNames.includes(name))) return false;
    }

    if (filters.productCategoryIds.length > 0) {
      if (!article.productCategoryId) return false;

      if (!filters.productCategoryIds?.includes(article.productCategoryId)) return false;
    }

    return true;
  }

  function filterArticlePickerProducts(
    filter: ArticleFilters,
    products: ArticlePickerProduct[]
  ): ArticlePickerProduct[] {
    return products
      .map((product) => ({
        ...product,
        articles: product.articles.filter((article) => filterMatchesArticlePickerArticle(filter, product, article)),
      }))
      .filter((product) => product.articles.length);
  }

  //#region Filter histograms (facets)
  // The raw facts behind the histogram sliders: for every article matching the
  // non-slider filters, its out-price amounts and its drop ids. The bars are
  // computed from these locally, so dragging a slider is live — no round trip —
  // and each slider excludes only itself from its own counts.
  const filterFacets = ref<FilterFacets | null>(null);

  // Null until the first load returns, which is what separates "no prices exist" from
  // "we do not know yet" — the sliders render an empty state for the former and a
  // skeleton for the latter. A reload keeps the previous facets, so this stays true and
  // the bars go stale rather than blank while a refetch is in flight.
  const facetsLoaded = computed(() => filterFacets.value !== null);

  // A reload keeps the previous facets on screen rather than blanking them, so the
  // sliders dim and stop taking input while fresh counts are on their way. Counted
  // rather than a boolean: a burst of filter changes can overlap requests, and the
  // first to return must not clear the flag while another is still out.
  const facetsInFlight = ref(0);
  const facetsLoading = computed(() => facetsInFlight.value > 0);

  // Overlapping requests can also come back out of order — a slow load for the filters the
  // user has already moved on from would otherwise land last and win, leaving the bars
  // counting a population nothing on screen matches. Only the newest request may write.
  let latestFacetRequest = 0;

  // The slider selections that shape the counts. The page that loads the facets
  // sets this to a getter for its live filter object — a getter rather than the
  // object itself, because pages rebuild their filter object when applying a URL
  // or clearing, and a captured reference silently goes stale — and clears it on
  // unmount. With nothing set the bars count the whole loaded population, which
  // is what surfaces without their own facet load (the article picker, samples)
  // show.
  const facetFilterContext = ref<(() => ArticleFilters) | null>(null);

  async function loadFilterFacets(activeSeasonId?: number | null, filters?: ArticleFilters) {
    const request = ++latestFacetRequest;
    facetsInFlight.value++;
    try {
      const fresh = await api.overview_GetFilterFacets(useUser().brandId, {
        activeSeasonId: activeSeasonId ?? undefined,
        filters: withoutSliderFilters(filters ?? getInitialFilters()) as BackendArticleFilters,
      });

      if (request === latestFacetRequest) filterFacets.value = fresh;
    } finally {
      facetsInFlight.value--;
    }
  }

  /** The filter shape the facets are loaded for (and reloads watched on): everything except the sliders' own selections. */
  function withoutSliderFilters(filters: ArticleFilters): ArticleFilters {
    return { ...filters, dropDateRange: null!, outPriceAmounts: {}, outPriceRanges: {} };
  }

  const outPriceKeys = computed(() => filterFacets.value?.outPriceKeys ?? []);

  const priceSets = computed(() =>
    pricePredicateSets(filterFacets.value?.outPriceRows ?? [], facetFilterContext.value?.() ?? {})
  );

  const dropSet = computed(() => {
    const dropDates = new Map(useDrops().loaded.map((d) => [d.id, new Date(d.dropDate).getTime()]));
    return dropPredicateSet(filterFacets.value?.dropRows ?? [], facetFilterContext.value?.().dropDateRange, dropDates);
  });

  function getOutPriceValues(key: string): number[] {
    return outPriceKeys.value.find((k) => k.key === key)?.allAmounts ?? [];
  }

  // Bars for one price histogram: shaped by every slider except this key's own.
  function getOutPriceAmountCounts(key: string): OutPriceAmountCount[] {
    const info = outPriceKeys.value.find((k) => k.key === key);
    if (!info) return [];
    const otherPriceSets = [...priceSets.value.entries()].filter(([k]) => k !== key).map(([, set]) => set);
    const eligible = intersectAll([...otherPriceSets, dropSet.value]);
    return outPriceAmountCounts(
      filterFacets.value?.outPriceRows ?? [],
      key,
      info.destinationId!,
      info.allAmounts ?? [],
      eligible
    );
  }

  // The drop histogram's bar: shaped by every price slider, never by the drop range itself.
  function getDropCount(dropId: number): number {
    const eligible = intersectAll([...priceSets.value.values()]);
    return dropCount(filterFacets.value?.dropRows ?? [], dropId, eligible);
  }
  //#endregion

  function anyFiltersApplied(filters: ArticleFilters) {
    return !isEqual(filters, getInitialFilters());
  }

  return {
    loadFilters,
    filterOptions,
    getInitialFilters,
    getActiveFiltersCount,
    anyFiltersApplied,
    filterArticlePickerProducts,
    outPriceKeys,
    facetsLoaded,
    facetsLoading,
    loadFilterFacets,
    withoutSliderFilters,
    facetFilterContext,
    getOutPriceValues,
    getOutPriceAmountCounts,
    getDropCount,
  };
});
